import * as React from "react";
import { useNavigate, useSearchParams } from "react-router";

import { ZodProvider } from "@autoform/zod";
import { useNotification } from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";
import { z } from "zod";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { CitySearchSelect } from "@/components/fields/city-search-select";
import { AutoForm } from "@/components/ui/autoform";
import { Button } from "@/components/ui/button";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { FilterSelect } from "@/pages/areas/components/filter-select";
import { useAreaCities } from "@/pages/areas/hooks/use-area-explore";
import { useAreaRepository } from "@/pages/areas/hooks/use-area-repository";
import { useReloadAreas } from "@/pages/areas/hooks/use-reload-areas";
import { areaNameSchema } from "@/pages/areas/schemas/area";
import { cityKey, parseCityKey } from "@/types/area";

const createSchema = z.object({ name: areaNameSchema });
const schemaProvider = new ZodProvider(createSchema);

/** New area by a curator. A city that has parts needs the area's part; the nightly adds the shape. */
export const AreaCreate = () => {
  useDocumentTitle()("New area");
  const repo = useAreaRepository();
  const reload = useReloadAreas();
  const navigate = useNavigate();
  const { open: notify } = useNotification();
  const { cities, isLoading: citiesLoading } = useAreaCities();
  const [params] = useSearchParams();

  // The list's filter travels as `?city=London&countryCode=GB`.
  const paramCity = params.get("city");
  const paramCountry = params.get("countryCode");
  const [city, setCity] = React.useState<string | undefined>(
    paramCity && paramCountry
      ? cityKey({ city: paramCity, countryCode: paramCountry })
      : undefined,
  );
  const [kind, setKind] = React.useState<"area" | "part">("area");
  const [parentId, setParentId] = React.useState<string>();
  const [error, setError] = React.useState<string>();
  const [saving, setSaving] = React.useState(false);

  // The picked city's parts come from its own documents (`tree`), so a part made a moment ago counts.
  const [parts, setParts] = React.useState<{ id: string; name: string }[]>([]);
  const [partsFor, setPartsFor] = React.useState<string>();
  const [partsError, setPartsError] = React.useState<string>();
  const repoRef = React.useRef(repo);
  repoRef.current = repo;
  React.useEffect(() => {
    setParts([]);
    setPartsFor(undefined);
    setPartsError(undefined);
    if (!city) return;
    const where = parseCityKey(city);
    let live = true;
    repoRef.current
      .tree(where.city, where.countryCode)
      .then((tree) => {
        if (!live) return;
        setParts(tree.parts.map((p) => ({ id: p.part.id, name: p.part.name })));
        setPartsFor(city);
      })
      .catch((e: unknown) => live && setPartsError(toHumanErrorMessage(e)));
    return () => {
      live = false;
    };
  }, [city]);
  // A city's parts decide whether an area needs one: wait for them, never guess.
  const partsReady = !city || partsFor === city;

  const picked = city ? parseCityKey(city) : undefined;
  const needsPart = kind === "area" && parts.length > 0;

  const onSubmit = async ({ name }: z.infer<typeof createSchema>) => {
    const where = city ? parseCityKey(city) : undefined;
    if (!where?.city || !where.countryCode) return setError("Choose a city");
    if (!partsReady) return setError(partsError ?? "Loading the city's parts");
    if (needsPart && !parentId) return setError("Choose a part");
    setError(undefined);
    setSaving(true);
    try {
      const { area, created } = await repo.create({
        ...where,
        name,
        kind,
        ...(kind === "area" && parentId ? { parentId } : {}),
        origin: "curator",
      });
      reload();
      notify?.({
        type: "success",
        message: created ? "Area created" : "Already exists",
        description: created ? undefined : `${area.name} was opened instead.`,
      });
      navigate(`/areas/edit/${encodeURIComponent(area.id)}`);
    } catch (e) {
      setError(toHumanErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex max-w-xl flex-col gap-4 p-8 pb-20">
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium">City</span>
        <CitySearchSelect
          options={cities}
          city={picked?.city ?? null}
          country={picked?.countryCode ?? null}
          onChange={(pick) => {
            setCity(pick ? cityKey(pick) : undefined);
            setParentId(undefined);
          }}
        />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium">Kind</span>
        <FilterSelect
          label="Kind"
          value={kind}
          placeholder="Choose a kind"
          className="w-full"
          options={[
            { value: "area", label: "Area" },
            { value: "part", label: "Part" },
          ]}
          onChange={(k) => setKind(k === "part" ? "part" : "area")}
        />
      </div>
      {partsError && (
        <p role="alert" className="text-sm text-destructive">
          Could not load the parts: {partsError}
        </p>
      )}
      {needsPart && (
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium">Part (required)</span>
          <FilterSelect
            label="Part"
            value={parentId}
            placeholder="Choose a part"
            className="w-full"
            options={parts.map((p) => ({ value: p.id, label: p.name }))}
            onChange={setParentId}
          />
        </div>
      )}
      <AutoForm schema={schemaProvider} onSubmit={onSubmit}>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <p className="text-sm text-muted-foreground">
          The map shape appears after Tina's next nightly run. Apps pick up
          changes within 1 hour.
        </p>
        <BottomActionBar>
          <Button
            type="submit"
            disabled={saving || citiesLoading || !partsReady}
          >
            Create
          </Button>
        </BottomActionBar>
      </AutoForm>
    </div>
  );
};
